import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
// import axios from 'axios';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import JobResult from './components/JobResult';
import Login from './components/Login';
import Signup from './components/Signup';
import About from './components/About';
// import VoiceInput from './components/VoiceInput';
import GovernmentJobs from './components/GovernmentJobs';
import SocialJobView from './components/SocialJobView';
import JobAlertModal from './components/JobAlertModal';
import JobTitleDropdown from './components/JobTitleDropdown';
import LocationDropdown from './components/LocationDropdown';
import ContactModal from './components/ContactModal';
// --- NEW IMPORTS FOR PASSWORD RESET ---
import ForgotPassword from './components/ForgotPassword';
import ResetPasswordConfirm from './components/ResetPasswordConfirm';
import ResumeBuilderPage from './components/resume-builder/ResumeBuilderPage';
import EmailVerification from './components/EmailVerification';
// --------------------------------------
import { FaSearch, FaMapMarkerAlt, FaBuilding, FaSignOutAlt, FaTrash, FaHistory, FaBullhorn, FaTimes, FaUserCircle, FaBell, FaFileAlt, FaCog, FaSun, FaMoon, FaShareAlt } from 'react-icons/fa';
import config from './config';
import FeedbackModal from "./components/FeedbackModal";
import { refreshTokenIfNeeded } from './utils/tokenUtils';
import apiClient from './utils/apiClient';

import LandingPage from './components/landing/NewLanding';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import Blogs from './components/Blogs';
import WalkinDrives from './components/WalkinDrives';
import SearchPage from './components/SearchPage';
import WalkinDriveDetail from './components/WalkinDriveDetail';
import WalkinDriveAdmin from './components/WalkinDriveAdmin';
import ProfilePage from './components/profile/ProfilePage';

import './style.css';
// import for new style and sidebar
import { AuroraBackground } from './components/AuroraBackground';
import { Sidebar, SidebarBody, SidebarLink } from './components/Sidebar';
import TechTipsTour from './components/TechTipsTour';
import { useSEO } from './hooks/useSEO';


// Social Job Login Prompt Component - shown when unauthenticated users try to access /social-job
const SocialJobLoginPrompt = ({ isDarkMode, c, toggleDarkMode, onLoginClick }) => {
  return (
    <div className={`min-h-screen transition-colors duration-300 ${c.pageBg} ${c.text}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="fixed top-6 right-6 z-50">
          <motion.button
            onClick={toggleDarkMode}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className={`p-3 rounded-full shadow-lg ${isDarkMode
              ? 'bg-[#23232A] text-[#06B6D4] hover:bg-[#252535]'
              : 'bg-white text-[#21808D] hover:bg-gray-100'
            }`}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </motion.button>
        </div>

        {/* Message about needing to login - hidden on mobile */}
        <div className="hidden md:block max-w-md mx-auto mb-6 mt-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-6 rounded-2xl border text-center ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
          >
            <div className="text-4xl mb-4">🔒</div>
            <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Sign up to view Social Jobs
            </h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Please sign up or sign in to access shared job opportunities.
            </p>
          </motion.div>
        </div>

        <Signup
          onBackToLogin={onLoginClick}
          isDarkMode={isDarkMode}
        />
      </div>
    </div>
  );
};


// MobileHeader Component
const MobileHeader = ({
  isDarkMode,
  onToggleHistory,
  onToggleDarkMode,
  onLogout,
  onProfileClick,
  onGoToGovJobs,
  onGoToResumeBuilder,
  onGoToSocialJobs,
  onShowJobAlertModal,
  onShowContactModal,
  isAuthenticated,
  onLogin
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Add touch handling to prevent double events
  const handleTouchAction = (callback) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Debounce to prevent double triggers
    if (e.target.dataset.processing) return;
    e.target.dataset.processing = 'true';

    setTimeout(() => {
      delete e.target.dataset.processing;
    }, 300);

    callback();
  };

 const navItems = isAuthenticated ? [
  { icon: <FaHistory />, label: "History", action: handleTouchAction(onToggleHistory) },
  { id: "mobile-tour-resume", icon: <FaFileAlt />, label: "Resume", action: handleTouchAction(onGoToResumeBuilder) },
  /* Temporarily hidden — will be used in the future
  { id: "mobile-tour-govt-jobs", icon: <FaBuilding />, label: "Gov Jobs", action: handleTouchAction(onGoToGovJobs) },
  { id: "mobile-tour-job-alerts", icon: <FaBell />, label: "Alerts", action: handleTouchAction(onShowJobAlertModal) },
  { icon: <FaShareAlt />, label: "Social Jobs", action: handleTouchAction(onGoToSocialJobs) },
  */
  { icon: <FaCog />, label: "Settings", action: handleTouchAction(() => setSettingsOpen(!settingsOpen)), isSettings: true },
  { icon: <FaSignOutAlt />, label: "Logout", action: handleTouchAction(onLogout), extraClass: "text-red-500 hover:bg-red-100/20" },
] : [
  { icon: <FaUserCircle />, label: "Sign Up", action: handleTouchAction(onLogin), extraClass: "text-blue-500" },
  /* Temporarily hidden — will be used in the future
  { id: "mobile-tour-govt-jobs", icon: <FaBuilding />, label: "Gov Jobs", action: handleTouchAction(onGoToGovJobs) },
  { icon: <FaShareAlt />, label: "Social Jobs", action: handleTouchAction(onGoToSocialJobs) },
  */
  { icon: <FaCog />, label: "Settings", action: handleTouchAction(() => setSettingsOpen(!settingsOpen)), isSettings: true },
];


  return (
    <>
      <motion.nav
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 120, damping: 12 }}
        className={`fixed top-4 inset-x-0 mx-auto max-w-md z-50
          flex justify-around items-center gap-6 px-4 py-3
          shadow-lg backdrop-blur-md rounded-full md:hidden
          ${isDarkMode ? "bg-[#000000cc] text-gray-100" : "bg-white/90 text-gray-800"}`}
      >
        {navItems.map((btn, idx) => (
          <div key={idx} className="relative" ref={btn.isSettings ? settingsRef : null}>
            <motion.button
              id={btn.id}
              onTouchEnd={btn.action}
              onClick={btn.action}
              className={`relative group flex items-center justify-center p-2 rounded-full transition
                hover:bg-gray-200/20 touch-manipulation ${btn.extraClass || ""}`}
              whileHover={{ scale: 1.25 }}
              whileTap={{ scale: 0.9 }}
              style={{ touchAction: 'manipulation' }}
            >
              {btn.icon}
              <span
                className="absolute left-1/2 -translate-x-1/2 mt-14 px-2 py-1 text-xs rounded-md
      bg-gray-800 text-white whitespace-nowrap opacity-0 scale-90
      group-hover:opacity-100 group-hover:scale-100 group-hover:mt-16
      transition-all duration-300"
              >
                {btn.label}
              </span>
            </motion.button>
          </div>
        ))}
      </motion.nav>

      {/* Settings Dropdown Modal */}
      <AnimatePresence>
        {settingsOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-start justify-center pt-20 md:hidden"
            onClick={() => setSettingsOpen(false)}
          >
            <div className="absolute inset-0 bg-black/30" />
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className={`relative rounded-2xl shadow-xl p-4 min-w-[200px]
                ${isDarkMode ? 'bg-[#1C1C1C] text-gray-100' : 'bg-white text-gray-900'}`}
            >
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold flex items-center gap-2">
                  <FaCog className="h-4 w-4" /> Settings
                </h3>
                <button
                  onClick={() => setSettingsOpen(false)}
                  className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              </div>

              <div className="flex flex-col gap-2">
                {isAuthenticated && (
                  <button
                    onClick={() => { onProfileClick(); setSettingsOpen(false); }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors
                      ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <FaUserCircle className="h-5 w-5" />
                    <span>My Profile</span>
                  </button>
                )}
                <button
                  onClick={() => { onToggleDarkMode(); setSettingsOpen(false); }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors
                    ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  {isDarkMode ? <FaSun className="h-5 w-5" /> : <FaMoon className="h-5 w-5" />}
                  <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

function App() {
  // ----------- React Router Hooks -----------
  const navigate = useNavigate();
  const locationHook = useLocation();

  // ----------- State Management ------------
  const [title, setTitle] = useState('string');
  const [locationInput, setLocationInput] = useState('');
  const [company, setCompany] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false); // Defaulted to true for initial load

  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [showJobAlertModal, setShowJobAlertModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState('');
  const [user, setUser] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [limit, setLimit] = useState(5);

  const [history, setHistory] = useState([]);

  // --- NEW STATES for Password Reset ---
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);
  // -------------------------------------

  // Guest mode notification
  const [showGuestNotification, setShowGuestNotification] = useState(false);

  // Guest mode search limit
  const GUEST_SEARCH_LIMIT_KEY = 'jobxai_guest_search_count';
  const [guestSearchCount, setGuestSearchCount] = useState(() => {
    try {
      return parseInt(localStorage.getItem(GUEST_SEARCH_LIMIT_KEY) || '0', 10);
    } catch {
      return 0;
    }
  });

  // side bar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const desktopResultsRef = useRef(null);
  const mobileResultsRef = useRef(null);
  const authCheckExecutedRef = useRef(false);
  const settingsRef = useRef(null);

  // Detect mobile for performance optimizations
  const isMobile = useMemo(() => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
  }, []);

  const LOCAL_HISTORY_KEY = 'jobxai_search_history';
  const loadLocalHistory = () => JSON.parse(localStorage.getItem(LOCAL_HISTORY_KEY) || '[]');
  const saveLocalHistory = (list) => localStorage.setItem(LOCAL_HISTORY_KEY, JSON.stringify(list));

  
  const [showLanding, setShowLanding] = useState(() => {
    try {
      // Parse both query and hash for OAuth params
      const searchParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const hasOAuth = searchParams.get('access_token') || searchParams.get('auth_success') || searchParams.get('error') || hashParams.get('access_token') || hashParams.get('auth_success') || hashParams.get('error');

      // Only consider showing landing on the root path
      if (window.location.pathname !== '/') return false;

      // If this load includes OAuth tokens or auth flags, don't show landing
      if (hasOAuth) return false;

      return !localStorage.getItem('hasVisitedJobxBharat');
    } catch (e) {
      return false;
    }
  });


  const colors = {
    light: {
      pageBg: 'bg-[#F5F5F5]',
      card: 'bg-white',
      border: 'border-gray-300',
      accent: 'bg-gray-900',
      gradientText: 'bg-clip-text text-transparent bg-gradient-to-r from-gray-800 to-black',
      text: 'text-gray-900',
      secondaryText: 'text-gray-600',
      button: 'bg-black text-white shadow-sm',
      buttonHover: 'hover:bg-gray-800 hover:shadow-md',
    },
    dark: {
      pageBg: 'bg-black',
      card: 'bg-[#1A1A1A]',
      border: 'border-gray-700',
      accent: 'bg-gray-300',
      gradientText: 'bg-clip-text text-transparent bg-gradient-to-r from-gray-400 to-gray-600',
      text: 'text-gray-100',
      secondaryText: 'text-gray-400',
      button: 'bg-gray-200 text-black shadow-sm',
      buttonHover: 'hover:bg-white hover:shadow-md',
    },
  };


  const c = useMemo(() => isDarkMode ? colors.dark : colors.light, [isDarkMode]);

  // ----------- API ------------
  // const api = useMemo(() => {
  //   const instance = apiClient.create({ baseURL: config.API_BASE_URL });
  //   if (authToken) instance.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
  //   return instance;
  // }, [authToken]);
  // Routes that set their own dynamic SEO (walk-in detail, search) opt out of the
  // global seoConfig-based call so they aren't overwritten by App's effect.
  const ownsOwnSeo =
    locationHook.pathname === '/search' ||
    /^\/jobs-in-.+/.test(locationHook.pathname) ||
    /^\/jobs\/.+/.test(locationHook.pathname) ||
    /^\/walkin-drives\/.+/.test(locationHook.pathname);
  useSEO({}, { skip: ownsOwnSeo });
  const api = apiClient;

  const fetchHistory = useCallback(() => {
    setHistory(loadLocalHistory());
  }, []);

  const addHistory = useCallback((payload) => {
    const entry = { job_title: payload.job_title, location: payload.location || '', organization: payload.organization || '' };
    const now = new Date().toISOString();
    const newList = [{ ...entry, created_at: now, title: entry.job_title }, ...loadLocalHistory()]
      .filter((item, idx, self) =>
        idx === self.findIndex(t => (t.title || '') === (item.title || '') && (t.location || '') === (item.location || '') && (t.organization || '') === (item.organization || ''))
      ).slice(0, 15);
    saveLocalHistory(newList);
    setHistory(newList);
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    saveLocalHistory([]);
  }, []);

  const searchJobs = useCallback(async () => {
  if (!title.trim()) return;

  // Check guest search limit
  if (!isAuthenticated && guestSearchCount >= 2) {
    setError('Search limit reached. Please sign in to continue searching for jobs.');
    return;
  }

  setLoading(true);
  setError(null);

  const payload = {
    job_title: title,
    location: locationInput && locationInput.trim() ? locationInput : "string",
    organization: company && company.trim() ? company : "string",
    limit,
  };

  const apiEndpoint = isAuthenticated ? '/search' : '/search_wt';

  try {
    const response = await apiClient.post(
      apiEndpoint,
      payload,
      {
        params: { source: 'recruiter', num_jobs: 20 },
        ...(authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}),
      }
    );
    setJobs(response.data || []);
    addHistory(payload);
    setIsHistoryVisible(false);

    // Increment guest search count only for unauthenticated users
    if (!isAuthenticated) {
      const newCount = guestSearchCount + 1;
      setGuestSearchCount(newCount);
      localStorage.setItem(GUEST_SEARCH_LIMIT_KEY, newCount.toString());
    }
  } catch {
    setError('Failed to fetch jobs. Please try again.');
  } finally {
    setLoading(false);
  }
}, [title, locationInput, company, authToken, addHistory, limit, isAuthenticated, guestSearchCount, GUEST_SEARCH_LIMIT_KEY]);


  // const handleCreateJobAlert = useCallback(async (alertData) => {
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     const response = await api.post('/job-alerts/', alertData);
  //     console.log('Job alert created:', response.data);
  //   } catch (err) {
  //     setError('Failed to create job alert. Please check your inputs and try again.');
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [api]);
    const handleCreateJobAlert = useCallback(async (alertData) => {
    try {
      const response = await api.post('/job-alerts/', alertData);
      return response.data;
    } catch (err) {
      throw err;
    }
  }, []);

  const handleInputKeyPress = useCallback((e) => { if (e.key === 'Enter') searchJobs(); }, [searchJobs]);
  const toggleDarkMode = useCallback(() => { const val = !isDarkMode; setIsDarkMode(val); document.body.classList.toggle('dark-mode', val); document.documentElement.classList.toggle('dark', val); }, [isDarkMode]);

  const handleGuestModeClick = useCallback(() => {
    setShowGuestNotification(true);
    setTimeout(() => setShowGuestNotification(false), 3000);
  }, []);

  useEffect(() => { document.body.classList.toggle('dark-mode', isDarkMode); document.documentElement.classList.toggle('dark', isDarkMode); return () => document.body.classList.remove('dark-mode'); }, [isDarkMode]);

  // --- Sync URL query params with modal states ---
  useEffect(() => {
  const path = locationHook.pathname;
  setShowFeedbackModal(false);
  setShowContactModal(false);
  setShowJobAlertModal(false);

  if (path === '/feedback') setShowFeedbackModal(true);
  else if (path === '/contact') setShowContactModal(true);
  else if (path === '/job-alert') setShowJobAlertModal(true);
}, [locationHook.pathname]);


  useEffect(() => {
      if (!loading && jobs.length > 0) {
        const isDesktop = window.matchMedia('(min-width: 768px)').matches;
        const target = isDesktop ? desktopResultsRef.current : mobileResultsRef.current;
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, [loading, jobs]);

  // --- Authentication/Token/Reset Check Effect ---
  useEffect(() => {
    // 1. Check for OAuth tokens first
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token');
    const refreshToken = urlParams.get('refresh_token');
    const authSuccess = urlParams.get('auth_success');
    const authError = urlParams.get('error');

    // Handle OAuth tokens immediately if present
    if (accessToken || authError || authSuccess) {
      setShowLanding(false); // Ensure landing page is hidden
      setIsLoading(false); // Allow auth components to render

      // If we have an access token and a successful auth flag, process it centrally here
      if (accessToken && authSuccess === 'true') {
        // Clean URL to remove tokens and auth params
        if (window.history.replaceState) {
          const u = new URL(window.location.href);
          u.searchParams.delete('access_token');
          u.searchParams.delete('refresh_token');
          u.searchParams.delete('auth_success');
          u.searchParams.delete('error');
          window.history.replaceState({}, document.title, u.pathname + u.search);
        }

        // Mark that the user has visited so LandingPage won't show during this OAuth flow
        try { localStorage.setItem('hasVisitedJobxBharat', 'true'); } catch (e) { /* ignore */ }
        setShowLanding(false);

        // Call central handler so App stores tokens and performs navigation to /s
        handleLoginSuccess({ access_token: accessToken, refresh_token: refreshToken });
        // The handleLoginSuccess function already navigates to /s, so we don't need to do it again here
        return;
      }

      // For other auth query states (errors etc.) just let auth UI render
      return;
    }

    // 2. Check for password reset token in URL on mount
    const token = urlParams.get('token');
    const path = window.location.pathname;

    if (token && path.includes('/reset-password')) {
      setShowResetPasswordConfirm(true);
      setShowForgotPassword(false);
      setShowSignup(false);
      setIsLoading(false);
      // Exit early, the user is in the reset flow
      return;
    }

    // 3. Load history
    fetchHistory();

    // Ensure reset forms are hidden if we are logging in/out normally
    setShowForgotPassword(false);
    setShowResetPasswordConfirm(false);

    // Note: Authentication check is handled by checkAuthStatus useEffect below
  }, []);

  // const handleLoginSuccess = (data) => {
  //   localStorage.setItem('token', data.access_token);
  //   setAuthToken(data.access_token);
  //   setIsAuthenticated(true);
  //
  //   // Hide all auth forms
  //   setShowSignup(false);
  //   setShowForgotPassword(false);
  //   setShowResetPasswordConfirm(false);
  //
  //   setError(null);
  //
  //   // Optional: Clean up URL after successful login (especially if coming from reset link)
  // //   if (window.history.replaceState) {
  // //       const url = new URL(window.location.href);
  // //       url.searchParams.delete('token');
  // //       // Replace the history state to remove the token from the URL bar
  // //       window.history.replaceState({}, document.title, url.pathname);
  // //   }
  // //
  // //   (async () => {
  // //     try {
  // //       const response = await apiClient.get(`${config.API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${data.access_token}` } });
  // //       setUser(response.data);
  // //     } catch {}
  // //   })();
  // // };
  //     // Optional: Clean up URL after successful login (especially if coming from reset link)
  //     if (window.history.replaceState) {
  //         const url = new URL(window.location.href);
  //         url.searchParams.delete('token');
  //         url.searchParams.delete('access_token');
  //         url.searchParams.delete('refresh_token');
  //         url.searchParams.delete('auth_success');
  //         url.searchParams.delete('error');
  //         // Replace the history state to remove the token from the URL bar
  //         window.history.replaceState({}, document.title, url.pathname);
  //     }
  //
  //     // Fetch user data
  //     (async () => {
  //       try {
  //         const response = await apiClient.get(`${config.API_BASE_URL}/auth/me`, {
  //           headers: { Authorization: `Bearer ${data.access_token}` }
  //         });
  //         setUser(response.data);
  //       } catch (error) {
  //         console.error('Failed to fetch user data:', error);
  //       }
  //     })();
  //   };

    const handleLoginSuccess = (data) => {
      try {
        localStorage.setItem('token', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }

        setAuthToken(data.access_token);
        setIsAuthenticated(true);

        // Hide all auth forms
        setShowSignup(false);
        setShowForgotPassword(false);
        setShowResetPasswordConfirm(false);

        // Clear any error states
        setError(null);

        // Fetch user data
        (async () => {
          try {
            const response = await apiClient.get(`${config.API_BASE_URL}/auth/me`, {
              headers: { Authorization: `Bearer ${data.access_token}` }
            });
            setUser(response.data);
          } catch (error) {
            // Don't logout on user fetch failure if token is valid
          }
        })();
        // Redirect back to where login was requested from (e.g. resume builder), else /search
        const postLoginRedirect = localStorage.getItem('postLoginRedirect');
        localStorage.removeItem('postLoginRedirect');
        navigate(postLoginRedirect || '/search', { replace: true });
      } catch (error) {
        setError('Login successful but failed to complete setup. Please refresh the page.');
      }
    };

  // const handleLogout = () => {
  //   localStorage.removeItem('token');
  //   localStorage.removeItem('refresh_token'); // Clear refresh token too
  //   setAuthToken('');
  //   setIsAuthenticated(false);
  //   setUser(null);
  //   setHistory(loadLocalHistory());
  // };

    const handleLogout = useCallback(() => {
      try {
        // Clear all storage
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');

        localStorage.removeItem('hasVisitedJobxBharat');

        // Reset all auth states
        setAuthToken('');
        setIsAuthenticated(false);
        setUser(null);

        // Reset other states
        setShowSignup(false);
        setShowForgotPassword(false);
        setShowResetPasswordConfirm(false);
        setError(null);

        // Load local history
        setHistory(loadLocalHistory());

        // Force a small delay to ensure state updates are processed
        setTimeout(() => {
          // Redirect to login page after logout
          navigate('/login');
        }, 100);

      } catch (error) {
        // Force redirect to /login as fallback
        window.location.replace(`${window.location.origin}/login`);
      }
    }, [navigate, loadLocalHistory]);

  const runHistoryItem = (item) => {
    setTitle(item.title || '');
    setLocationInput(item.location || '');
    setCompany(item.organization || '');
    setTimeout(() => searchJobs(), 0);
  };

  const jobResults = useMemo(() => (
    jobs.slice(0, limit).map((job, index) => (
      <motion.div
        key={`job-${job.id || index}`}
        initial={isMobile ? {} : { opacity: 0, y: 20 }}
        animate={isMobile ? {} : { opacity: 1, y: 0 }}
        exit={isMobile ? {} : { opacity: 0, y: -20 }}
        transition={isMobile ? {} : { duration: 0.3, delay: index * 0.05 }}
      >
        <JobResult job={job} isDarkMode={isDarkMode} isGuest={!isAuthenticated} />
      </motion.div>
    ))
  ), [jobs, isDarkMode, limit, isMobile, isAuthenticated]);

  const handleBackToHome = () => navigate('/');
  const handleGoToGovJobs = () => navigate('/gov-jobs');
  const handleGoToAbout = () => navigate('/about');

  // Modal handlers with URL updates
  const openModal = (modalName) => {
      navigate(`/${modalName}`);
  };

  const closeModal = () => {
  navigate('/search');
    // Close all modals
    setShowFeedbackModal(false);
    setShowContactModal(false);
    setShowJobAlertModal(false);
  };

  // Update the authentication check useEffect
    useEffect(() => {
      const checkAuthStatus = async () => {
        // Prevent double execution in React 18 Strict Mode
        if (authCheckExecutedRef.current) {
          return;
        }
        authCheckExecutedRef.current = true;

        let authenticated = false;
        try {
          const token = localStorage.getItem('token');

          if (token) {
            // Try to refresh token if needed
            const isValid = await refreshTokenIfNeeded();

            if (isValid) {
              authenticated = true;
              setIsAuthenticated(true);
              setAuthToken(token);

              // Try to fetch user data
              try {
                const response = await apiClient.get(`${config.API_BASE_URL}/auth/me`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                setUser(response.data);
              } catch (error) {
                // Could not fetch user data, but token is valid
              }
            } else {
              // Inline logout to avoid dependency issues
              localStorage.removeItem('token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('hasVisitedJobxBharat');
              setAuthToken('');
              setIsAuthenticated(false);
              setUser(null);
              setShowSignup(false);
              setShowForgotPassword(false);
              setShowResetPasswordConfirm(false);
              setError(null);
              setHistory(loadLocalHistory());
            }
          } else {
            setIsAuthenticated(false);
          }
        } catch (error) {
          setIsAuthenticated(false);
        } finally {
          // Determine landing visibility atomically with loading state to prevent flash
          if (window.location.pathname === '/') {
            const sp = new URLSearchParams(window.location.search);
            const hasOAuth = sp.get('access_token') || sp.get('auth_success') || sp.get('error');
            if (!hasOAuth && !authenticated) {
              setShowLanding(true);
            }
          }
          setIsLoading(false);
        }
      };

      checkAuthStatus();
    }, []); // Empty dependency array - only run once on mount

  // Fire initial search on page load once auth check is done
  useEffect(() => {
    if (!isLoading) {
      searchJobs();
    }
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
  if (isLoading) return; // ✅ Don't run until auth check is complete

  // Consider both search and hash params (some OAuth providers return tokens in the hash)
  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const hasOAuthTokens = searchParams.get('access_token') || searchParams.get('error') || searchParams.get('auth_success') || hashParams.get('access_token') || hashParams.get('error') || hashParams.get('auth_success');

  if (hasOAuthTokens) {
    setShowLanding(false);
    return;
  }

  // Only show the landing page when the user is on the canonical root path '/'
  const path = locationHook.pathname;
  if (path !== '/') {
    setShowLanding(false);
    return;
  }

  const hasVisited = localStorage.getItem('hasVisitedJobxBharat');

  if (isAuthenticated) {
    // ✅ User is logged in → never show landing
    setShowLanding(false);
    return;
  }

  // ✅ User is not logged in and is on '/'
  setShowLanding(true);
}, [isAuthenticated, isLoading, locationHook.pathname]);

  // For Seo Meta Tags
  useEffect(() => {
   const path = locationHook.pathname;
   const baseUrl = 'https://www.jobxbharat.com';

   // Remove existing canonical link
   const existingCanonical = document.querySelector('link[rel="canonical"]');
   if (existingCanonical) {
      existingCanonical.remove();
   }

   // Add new canonical link
   const canonical = document.createElement('link');
   canonical.rel = 'canonical';
   canonical.href = `${baseUrl}${path}`;
   document.head.appendChild(canonical);
   }, [locationHook.pathname]);


  useEffect(() => {
    const intervalId = setInterval(async () => {
      const token = localStorage.getItem('token');
      if (token) {
        const refreshed = await refreshTokenIfNeeded();
        if (!refreshed) {
          // Inline logout to avoid dependency issues
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('hasVisitedJobxBharat');
          setAuthToken('');
          setIsAuthenticated(false);
          setUser(null);
          setShowSignup(false);
          setShowForgotPassword(false);
          setShowResetPasswordConfirm(false);
          setError(null);
          setHistory(loadLocalHistory());

          // Navigate to home if on protected route
          if (window.location.pathname !== '/') {
            navigate('/search');
          }
        }
      }
    }, 600000); // Refresh every 10 minutes (30 mins token expiry - 20 mins buffer)
    return () => clearInterval(intervalId);
  }, []); // Empty dependency array

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  // --- Authentication Component Renderer ---
  const renderAuthComponent = () => {
    // 1. Password Reset Confirmation (Highest Priority if token is in URL)
    if (showResetPasswordConfirm) {
      return (
        <ResetPasswordConfirm
          onLoginClick={() => setShowResetPasswordConfirm(false)}
          isDarkMode={isDarkMode}
        />
      );
    }
    // 2. Forgot Password Request
    if (showForgotPassword) {
      return (
        <ForgotPassword
          onBackToLogin={() => setShowForgotPassword(false)}
          isDarkMode={isDarkMode}
        />
      );
    }
    // 3. Signup - check both state and URL path
    if (showSignup || locationHook.pathname === '/signup') {
      return (
        <Signup
          onBackToLogin={() => {
            setShowSignup(false);
            navigate('/login');
          }}
          isDarkMode={isDarkMode}
        />
      );
    }
    // 4. Default Login
    return (
      <Login
        onLoginSuccess={handleLoginSuccess}
        onRegisterClick={() => {
          setShowSignup(true);
          navigate('/signup');
        }}
        onForgotPasswordClick={() => setShowForgotPassword(true)}
        isDarkMode={isDarkMode}
      />
    );
  };
  // -----------------------------------------

  const handleGoToResumeBuilder = () => navigate('/resume-builder');
  const handleGoToSocialJobs = () => navigate('/social-job');

  // Sidebar links based on authentication status
  const sidebarLinks = isAuthenticated ? [
    {
      label: "Settings",
      href: "#",
      icon: <FaCog className="text-neutral-700 dark:text-neutral-200 h-5 w-5" />,
      onClick: () => setSettingsOpen(!settingsOpen),
      isSettings: true
    },
    {
      id: "tour-resume-builder",
      label: "Resume Builder",
      href: "#",
      icon: <FaFileAlt className="text-neutral-700 dark:text-neutral-200 h-5 w-5" />,
      onClick: handleGoToResumeBuilder
    },
    /* Temporarily hidden — will be used in the future
    {
      id: "tour-govt-jobs",
      label: "Government Jobs",
      href: "#",
      icon: <FaBullhorn className="text-neutral-700 dark:text-neutral-200 h-5 w-5" />,
      onClick: handleGoToGovJobs
    },
    {
      id: "tour-job-alerts",
      label: "Job Alerts",
      href: "#",
      icon: <FaBell className="text-neutral-700 dark:text-neutral-200 h-5 w-5" />,
      onClick: () => openModal('job-alert')
    },
    {
      label: "Social Job View",
      href: "#",
      icon: <FaShareAlt className="text-neutral-700 dark:text-neutral-200 h-5 w-5" />,
      onClick: handleGoToSocialJobs
    },
    */
    {
      label: "Logout",
      href: "#",
      icon: <FaSignOutAlt className="text-red-500 dark:text-red-400 h-5 w-5" />,
      onClick: handleLogout
    }
  ] : [
    {
      label: "Settings",
      href: "#",
      icon: <FaCog className="text-neutral-700 dark:text-neutral-200 h-5 w-5" />,
      onClick: () => setSettingsOpen(!settingsOpen),
      isSettings: true
    },
    {
      label: "Sign Up",
      href: "#",
      icon: <FaUserCircle className="text-neutral-700 dark:text-neutral-200 h-5 w-5" />,
      onClick: () => navigate('/signup')
    },
    {
      id: "tour-resume-builder",
      label: "Resume Builder",
      href: "#",
      icon: <FaFileAlt className="text-neutral-700 dark:text-neutral-200 h-5 w-5" />,
      onClick: () => navigate('/signup')
    },
    /* Temporarily hidden — will be used in the future
    {
      id: "tour-govt-jobs",
      label: "Government Jobs",
      href: "#",
      icon: <FaBullhorn className="text-neutral-700 dark:text-neutral-200 h-5 w-5" />,
      onClick: handleGoToGovJobs
    },
    {
      id: "tour-job-alerts",
      label: "Job Alerts",
      href: "#",
      icon: <FaBell className="text-neutral-700 dark:text-neutral-200 h-5 w-5" />,
      onClick: () => navigate('/signup')
    },
    {
      label: "Social Job View",
      href: "#",
      icon: <FaShareAlt className="text-neutral-700 dark:text-neutral-200 h-5 w-5" />,
      onClick: () => navigate('/signup')
    }
    */
  ];




const renderContent = () => {
  return (
    <div className={`${isDarkMode ? 'dark' : ''}`}>
      {/* Tech Tips Tour - shows on first visit */}
      <TechTipsTour isDarkMode={isDarkMode} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} isAuthenticated={isAuthenticated} />

      {/* Desktop Layout with Sidebar */}
      <div className="hidden md:flex h-screen">
        {/* Sidebar */}
        <Sidebar open={sidebarOpen} setOpen={setSidebarOpen}>
            <SidebarBody className="justify-between gap-10">
              <div className="flex flex-col flex-1 overflow-hidden">
                {sidebarOpen ? (
                  <div className="mt-8 mb-10">
                    <h2 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200">
                      Jobx<span className="text-brand-saffron">Bharat</span>
                    </h2>
                  </div>
                ) : (
                  <div className="mt-8 mb-10">
                    <img
                      src={isDarkMode ? require('./assets/logo.png') : "https://jobx-image.s3.ap-south-1.amazonaws.com/Group+166.svg"}
                      alt="JobxBharat"
                      className="h-8 w-8 object-contain"
                    />
                  </div>
                )}

                {/* Menu Items */}
                <div className="flex flex-col gap-2 mb-4">
                  {sidebarLinks.map((link, idx) => (
                    <div key={idx}>
                      <SidebarLink link={link} onClick={link.onClick} id={link.id} />
                      {/* Settings Dropdown */}
                      {link.isSettings && settingsOpen && sidebarOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="ml-6 mt-1 flex flex-col gap-1"
                        >
                          {isAuthenticated && (
                            <button
                              onClick={() => { navigate('/profile'); setSettingsOpen(false); }}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors
                                ${isDarkMode ? 'hover:bg-neutral-700 text-neutral-300' : 'hover:bg-neutral-200 text-neutral-700'}`}
                            >
                              <FaUserCircle className="h-4 w-4" />
                              <span>My Profile</span>
                            </button>
                          )}
                          <button
                            onClick={() => { toggleDarkMode(); setSettingsOpen(false); }}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors
                              ${isDarkMode ? 'hover:bg-neutral-700 text-neutral-300' : 'hover:bg-neutral-200 text-neutral-700'}`}
                          >
                            {isDarkMode ? <FaSun className="h-4 w-4" /> : <FaMoon className="h-4 w-4" />}
                            <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                          </button>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>

                {/* History Section — this fills remaining space */}
                {sidebarOpen && (
                  <div className="flex flex-col flex-1 min-h-0 border-t border-neutral-300 dark:border-neutral-700 pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <FaHistory className="text-neutral-700 dark:text-neutral-200 h-4 w-4" />
                        <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                          Recent Searches
                        </h3>
                      </div>
                      {history.length > 0 && (
                        <button
                          onClick={clearHistory}
                          className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          title="Clear history"
                        >
                          <FaTrash className="h-3 w-3" />
                        </button>
                      )}
                    </div>

                    <div className="flex-1 overflow-y-auto">
                      {history.length === 0 ? (
                        <p className="text-neutral-600 dark:text-neutral-400 text-xs">
                          No searches yet
                        </p>
                      ) : (
                        <ul className="space-y-2">
                          {history.slice(0, 50).map((item, idx) => (
                            <li key={item.id || `${item.title}-${idx}`}>
                              <button
                                onClick={() => runHistoryItem(item)}
                                className="w-full text-left p-2 rounded-lg text-xs hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
                              >
                                <div className="font-medium text-neutral-800 dark:text-neutral-200 truncate">
                                  {item.title || 'Any title'}
                                </div>
                                <div className="text-neutral-600 dark:text-neutral-400 truncate">
                                  {(item.location || 'Anywhere')} • {(item.organization || 'Any company')}
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
          </SidebarBody>
        </Sidebar>

        {/* Desktop Main Content */}
        <div className="flex-1 overflow-y-auto">
          <AuroraBackground className={isDarkMode ? 'dark' : ''}>
            <div className={`relative min-h-screen flex flex-col items-center justify-center`}>
              {/* Top right controls - Guest Mode badge only */}
              {!isAuthenticated && (
                <div className="fixed top-4 right-4 sm:top-8 sm:right-8 z-50">
                  <motion.button
                    onClick={handleGuestModeClick}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-4 py-2 rounded-full shadow-lg font-medium text-sm
                      ${isDarkMode ? 'bg-[#23232A] text-[#06B6D4] hover:bg-[#252535]' : 'bg-white text-[#5C6AC4] hover:bg-gray-100'}`}
                  >
                    Guest Mode
                  </motion.button>
                </div>
              )}

              {/* Guest Mode Notification */}
              <AnimatePresence>
                {showGuestNotification && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="fixed top-20 right-4 sm:top-24 sm:right-8 z-50 max-w-sm"
                  >
                    <div className={`p-4 rounded-lg shadow-lg ${isDarkMode ? 'bg-[#23232A] text-gray-100' : 'bg-white text-gray-900'}`}>
                      <p className="font-medium">You are in Guest Mode</p>
                      <p className="text-sm mt-1 opacity-80">Sign up to use all the features</p>
                      <motion.button
                        onClick={() => {
                          setShowGuestNotification(false);
                          navigate('/signup');
                        }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`mt-3 w-full px-4 py-2 rounded-lg font-medium text-sm transition-colors
                          ${isDarkMode ? 'bg-[#06B6D4] text-white hover:bg-[#0891B2]' : 'bg-[#5C6AC4] text-white hover:bg-[#4F5BB5]'}`}
                      >
                        Sign Up
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="container mx-0 px-4 sm:px-6 lg:px-8 pt-24 pb-12">
                <motion.header
                  className="text-center mb-10 pt-10 sm:pt-12"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="relative inline-block">
                    <h1 className={`text-4xl sm:text-6xl font-extrabold mb-2 ${c.text}`}>
                      <span className={c.gradientText}>JobxBharat</span>
                    </h1>
                    <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-gray-400 to-gray-600 rounded-full opacity-70"></div>
                  </div>
                  <p className={`${c.secondaryText} text-lg mt-4`}>Find your dream job with our powerful AI-powered search tool</p>
                </motion.header>

                {/* Search Form and Results */}
                <section className="max-w-4xl mx-auto">
                  <motion.div className={`max-w-4xl mx-auto ${jobs.length === 0 ? 'mt-0' : 'mb-8'}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
                    <div id="tour-job-search" className={`${c.card} ${c.border} flex flex-col gap-6 p-6 sm:p-8 rounded-2xl search-container w-full max-w-4xl`}>
                      {/* Search form inputs remain the same */}
                      <div className="relative">
                        <label className={`block text-sm font-medium mb-2 ${c.text}`}>
                          Job Title <span className="text-red-500">*</span>
                        </label>
                        <JobTitleDropdown
                          value={title}
                          onChange={setTitle}
                          onKeyPress={handleInputKeyPress}
                          isDarkMode={isDarkMode}
                        />
                      </div>

                      <div className="relative">
                        <label className={`block text-sm font-medium mb-2 ${c.text}`}>
                          Location <span className={`text-sm ${c.secondaryText}`}>(optional)</span>
                        </label>
                        <LocationDropdown
                          value={locationInput}
                          onChange={setLocationInput}
                          onKeyPress={handleInputKeyPress}
                          isDarkMode={isDarkMode}
                        />
                      </div>

                      <div className="relative">
                        <label className={`block text-sm font-medium mb-2 ${c.text}`}>
                          Company <span className={`text-sm ${c.secondaryText}`}>(optional)</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <FaBuilding className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} h-5 w-5`} />
                          </div>
                          <input
                            type="text"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            onKeyPress={handleInputKeyPress}
                            placeholder="e.g., Google, Microsoft"
                            className={`w-full pl-12 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all
                              ${isDarkMode
                                ? 'bg-[#1C1C1C] border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                                : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                              }`}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-4">
                        <div className="flex items-center gap-2">
                          <label className={`block text-sm font-medium ${c.text}`}>Show:</label>
                          <select
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value))}
                            className={`px-3 py-2 rounded-lg border transition-all cursor-pointer
                              ${isDarkMode
                                ? 'bg-[#1C1C1C] border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                                : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                              }`}
                          >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={15}>15</option>
                          </select>
                        </div>
                        <motion.button
                          onClick={searchJobs}
                          disabled={loading || !title.trim()}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          className={`w-full sm:w-auto px-8 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2
                            ${loading
                              ? 'opacity-70 cursor-not-allowed'
                              : c.button + ' ' + c.buttonHover
                            }`}
                          aria-label="Search for jobs"
                        >
                          <FaSearch className="h-4 w-4" /> {loading ? 'Searching...' : 'Search Jobs'}
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>

                  {/* Loading, Error, Results sections remain the same */}
                  {loading && (
                    <motion.div className="flex justify-center my-12" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                      <div className="relative">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-opacity-20"></div>
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-transparent border-l-transparent border-[#21808D] absolute top-0 left-0"></div>
                      </div>
                    </motion.div>
                  )}

                  {error && (
                    <motion.div className={`max-w-4xl my-8 p-6 rounded-2xl border shadow-md ${isDarkMode ? 'bg-red-900/30 border-red-800 text-red-200' : 'bg-red-100 border-red-200 text-red-700'}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path></svg>
                          <span className="font-medium">{error}</span>
                        </div>
                        {error.includes('Search limit reached') && (
                          <motion.button
                            onClick={() => navigate('/login')}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`ml-4 px-4 py-2 rounded-lg font-semibold whitespace-nowrap ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                          >
                            Sign In
                          </motion.button>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {!loading && !error && jobs.length === 0 && title.trim() && (
                    <motion.div
                      className={`max-w-4xl mx-auto my-8 p-8 rounded-2xl border shadow-md text-center ${isDarkMode ? 'bg-gray-800/50 border-gray-700 text-gray-300' : 'bg-blue-50 border-blue-200 text-gray-700'}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex flex-col items-center">
                        <svg className="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <h3 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          Currently, no jobs are available
                        </h3>
                        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Currently, there are no jobs available for the selected location and company. Please try adjusting your search criteria or filters.
                        </p>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          <p>Tips:</p>
                          <ul className="mt-2 text-left inline-block">
                            <li>• Try using different job titles or keywords</li>
                            <li>• Remove location or company filters</li>
                            <li>• Use more general search terms</li>
                          </ul>
                        </div>
                      </div>
                    </motion.div>
                  )}

                 {!loading && jobs.length > 0 && (
                  <motion.div
                    ref={desktopResultsRef}
                    className="mt-8 max-w-5xl mx-auto"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h2 className={`text-2xl sm:text-3xl font-bold ${c.text}`}>Job Results</h2>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto overflow-x-hidden pr-2 custom-scrollbar">
                      <div className="grid grid-cols-1 gap-6 md:gap-8">
                        <AnimatePresence>{jobResults}</AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                )}
                </section>

                <footer className={`text-center py-6 mt-10  border-t ${isDarkMode ? 'border-gray-800 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
                  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center">
                    <p className="text-sm mb-2 sm:mb-0"><span className="font-medium">~Made with dreams by JobxBharat</span></p>

                    <div className="flex space-x-4">
                      <a onClick={handleGoToAbout} className={`${c.secondaryText} hover:text-[#21808D] text-sm hover:underline cursor-pointer`}>About</a>
                      <a onClick={() => openModal('contact')} className={`${c.secondaryText} hover:text-[#5C6AC4] text-sm hover:underline cursor-pointer`}>Contact</a>
                     <a
                        onClick={() => openModal('feedback')}
                        className={`${c.secondaryText} hover:text-[#A855F7] text-sm hover:underline cursor-pointer`}
                      >
                        Feedback
                      </a>
                    </div>
                  </div>
                </footer>
              </div>
            </div>
          </AuroraBackground>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden">
        <AuroraBackground className={isDarkMode ? 'dark' : ''}>
          <div className={`relative min-h-screen flex flex-col items-center justify-center`}>
            {/* Mobile Header */}
            <MobileHeader
              isDarkMode={isDarkMode}
              user={user}
              onToggleHistory={() => setIsHistoryVisible(!isHistoryVisible)}
              onToggleDarkMode={toggleDarkMode}
              onLogout={handleLogout}
              onProfileClick={() => navigate('/profile')}
              onGoToGovJobs={handleGoToGovJobs}
              onGoToResumeBuilder={handleGoToResumeBuilder}
              onGoToSocialJobs={handleGoToSocialJobs}
              onShowJobAlertModal={() => openModal('job-alert')}
              onShowContactModal={() => openModal('contact')}
              isAuthenticated={isAuthenticated}
              onLogin={() => navigate('/signup')}
              c={c}
            />

            {/* Mobile History Modal */}
            <AnimatePresence>
              {isHistoryVisible && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="fixed inset-0 z-40 flex items-center justify-center"
                >
                  <div
                    className="absolute inset-0 bg-black/50"
                    onClick={() => setIsHistoryVisible(false)}
                  />
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`relative w-full max-w-md max-h-[80vh] overflow-auto rounded-2xl p-6 shadow-xl mx-4
                      ${isDarkMode ? "bg-[#191921] text-gray-100" : "bg-white text-gray-900"}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold flex items-center gap-2">
                        <FaHistory /> Recent Searches
                      </h3>
                      <motion.button
                        onClick={() => setIsHistoryVisible(false)}
                        className="p-2 rounded-full hover:bg-gray-200/20"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FaTimes />
                      </motion.button>
                    </div>
                    {history.length === 0 ? (
                      <p className="text-sm opacity-70">No searches yet. Let's find some jobs!</p>
                    ) : (
                      <ul className="space-y-3 pr-2">
                        {history.map((item, idx) => (
                          <li key={item.id || `${item.title}-${idx}`}>
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => { runHistoryItem(item); setIsHistoryVisible(false); }}
                              className={`w-full text-left px-4 py-3 rounded-xl border history-item transition-all duration-200
                                ${isDarkMode
                                  ? 'bg-[#23232A] hover:bg-[#252535] border-gray-600 shadow-sm-dark'
                                  : 'bg-gray-50 hover:bg-gray-100 border-gray-200 shadow-sm-light'
                                }`}
                            >
                              <div className="font-semibold truncate">{item.title || 'Any title'}</div>
                              <div className={`${c.secondaryText} text-sm truncate`}>
                                {(item.location || 'Anywhere')} • {(item.organization || 'Any company')}
                              </div>
                              {item.created_at && (
                                <div className={`${c.secondaryText} text-xs mt-1`}>
                                  {new Date(item.created_at).toLocaleString()}
                                </div>
                              )}
                            </motion.button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-6 flex justify-center">
                      <button
                        onClick={clearHistory}
                        className={`text-sm inline-flex items-center gap-1 px-4 py-2 rounded-full font-medium transition-colors
                          ${isDarkMode ? "bg-[#23232A] hover:bg-[#25232A] text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-600"}`}
                      >
                        <FaTrash className="text-sm" /> Clear All History
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="container mx-0 px-4 sm:px-6 lg:px-8 pt-24 pb-12">
              <motion.header
                className="text-center mb-10 pt-10 sm:pt-12"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative inline-block">
                  <h1 className={`text-4xl sm:text-6xl font-extrabold mb-2 ${c.text}`}>
                    <span className={c.gradientText}>JobxBharat</span>
                  </h1>
                  <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-gray-400 to-gray-600 rounded-full opacity-70"></div>
                </div>
                <p className={`${c.secondaryText} text-lg mt-4`}>Find your dream job with our powerful AI-powered search tool</p>
              </motion.header>

              {/* Mobile Search Form and Results - same as desktop */}
              <section className="max-w-4xl mx-auto">
                <motion.div className={`max-w-4xl mx-auto ${jobs.length === 0 ? 'mt-0' : 'mb-8'}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
                  <div id="mobile-tour-job-search" className={`${c.card} ${c.border} flex flex-col gap-6 p-6 sm:p-8 rounded-2xl search-container w-full max-w-4xl`}>
                    {/* Same search form as desktop... */}
                    <div className="relative">
                      <label className={`block text-sm font-medium mb-2 ${c.text}`}>
                        Job Title <span className="text-red-500">*</span>
                      </label>
                      <JobTitleDropdown
                        value={title}
                        onChange={setTitle}
                        onKeyPress={handleInputKeyPress}
                        isDarkMode={isDarkMode}
                      />
                    </div>

                    <div className="relative">
                      <label className={`block text-sm font-medium mb-2 ${c.text}`}>
                        Location <span className={`text-sm ${c.secondaryText}`}>(optional)</span>
                      </label>
                      <LocationDropdown
                        value={locationInput}
                        onChange={setLocationInput}
                        onKeyPress={handleInputKeyPress}
                        isDarkMode={isDarkMode}
                      />
                    </div>

                    <div className="relative">
                      <label className={`block text-sm font-medium mb-2 ${c.text}`}>
                        Company <span className={`text-sm ${c.secondaryText}`}>(optional)</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <FaBuilding className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} h-5 w-5`} />
                        </div>
                        <input
                          type="text"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          onKeyPress={handleInputKeyPress}
                          placeholder="e.g., Google, Microsoft"
                          className={`w-full pl-12 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all
                            ${isDarkMode
                              ? 'bg-[#1C1C1C] border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                              : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                            }`}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-4">
                      <div className="flex items-center gap-2">
                        <label className={`block text-sm font-medium ${c.text}`}>Show:</label>
                        <select
                          value={limit}
                          onChange={(e) => setLimit(Number(e.target.value))}
                          className={`px-3 py-2 rounded-lg border transition-all cursor-pointer
                            ${isDarkMode
                              ? 'bg-[#1C1C1C] border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                              : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                            }`}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={15}>15</option>
                        </select>
                      </div>
                      <motion.button
                        onClick={searchJobs}
                        disabled={loading || !title.trim()}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className={`w-full sm:w-auto px-8 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2
                          ${loading
                            ? 'opacity-70 cursor-not-allowed'
                            : c.button + ' ' + c.buttonHover
                          }`}
                        aria-label="Search for jobs"
                      >
                        <FaSearch className="h-4 w-4" /> {loading ? 'Searching...' : 'Search Jobs'}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>

                {/* Same loading, error, results sections as desktop */}
                {loading && (
                  <motion.div className="flex justify-center my-12" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                    <div className="relative">
                      <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-opacity-20"></div>
                      <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-transparent border-l-transparent border-[#21808D] absolute top-0 left-0"></div>
                    </div>
                  </motion.div>
                )}

                {error && (
                  <motion.div className={`max-w-4xl my-8 p-6 rounded-2xl border shadow-md ${isDarkMode ? 'bg-red-900/30 border-red-800 text-red-200' : 'bg-red-100 border-red-200 text-red-700'}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center">
                        <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path></svg>
                        <span className="font-medium">{error}</span>
                      </div>
                      {error.includes('Search limit reached') && (
                        <motion.button
                          onClick={() => navigate('/login')}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                        >
                          Sign In
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                )}

                {!loading && !error && jobs.length === 0 && title.trim() && (
                  <motion.div
                    className={`max-w-4xl mx-auto my-8 p-8 rounded-2xl border shadow-md text-center ${isDarkMode ? 'bg-gray-800/50 border-gray-700 text-gray-300' : 'bg-blue-50 border-blue-200 text-gray-700'}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex flex-col items-center">
                      <svg className="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <h3 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        No Jobs Found
                      </h3>
                      <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        We couldn't find any jobs matching your search criteria. Try adjusting your search terms or filters.
                      </p>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        <p>Tips:</p>
                        <ul className="mt-2 text-left inline-block">
                          <li>• Try using different job titles or keywords</li>
                          <li>• Remove location or company filters</li>
                          <li>• Use more general search terms</li>
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}

                {!loading && jobs.length > 0 && (
                  <motion.div
                    ref={mobileResultsRef}
                    className="mt-8 max-w-5xl mx-auto"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h2 className={`text-2xl sm:text-3xl font-bold ${c.text}`}>Job Results</h2>
                      <span className={`text-sm px-4 py-2 rounded-full font-medium ${isDarkMode ? 'bg-[#23232A] text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                        {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'} found
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:gap-8">
                      <AnimatePresence>{jobResults}</AnimatePresence>
                    </div>
                  </motion.div>
                )}

              </section>

              <footer className={`text-center py-6 mt-10 border-t ${isDarkMode ? 'border-gray-800 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center">
                  <p className="text-sm mb-2 sm:mb-0"><span className="font-medium">~Made with dreams by Jobx<span className="text-brand-saffron">Bharat</span></span></p>

                  <div className="flex space-x-4">
                    <a onClick={handleGoToAbout} className={`${c.secondaryText} hover:text-[#21808D] text-sm hover:underline cursor-pointer`}>About</a>
                    <a onClick={() => openModal('contact')} className={`${c.secondaryText} hover:text-[#5C6AC4] text-sm hover:underline cursor-pointer`}>Contact</a>
                   <a
                      onClick={() => openModal('feedback')}
                      className={`${c.secondaryText} hover:text-[#A855F7] text-sm hover:underline cursor-pointer`}
                    >
                      Feedback
                    </a>
                  </div>
                </div>
              </footer>
            </div>
          </div>
        </AuroraBackground>
      </div>

      {/* Modals (shown on both mobile and desktop) */}
       {showJobAlertModal && (
        <JobAlertModal
          onClose={closeModal}
          onCreate={handleCreateJobAlert}
          isDarkMode={isDarkMode}
        />
      )}
       {showContactModal && (
        <ContactModal
          onClose={closeModal}
          isDarkMode={isDarkMode}
        />
      )}
      {showFeedbackModal && (
        <FeedbackModal
          onClose={closeModal}
          isDarkMode={isDarkMode}
          user={user}
        />
      )}
    </div>
  );
};
if (showLanding) {
  // If OAuth params are present in the URL, don't render the LandingPage —
  // allow the auth callback logic to run and redirect the user.
  try {
    const _params = new URLSearchParams(window.location.search);
    const hasOAuthParams = _params.get('access_token') || _params.get('auth_success') || _params.get('error');
    if (hasOAuthParams) {
      // skip showing landing so OAuth handling proceeds
    } else {
      return (
        <LandingPage
          isDarkMode={isDarkMode}
          onSignIn={() => {
            setShowLanding(false);
            localStorage.setItem('hasVisitedJobxBharat', 'true');
            navigate('/login');
          }}
          onStartSearching={() => {
            setShowLanding(false);
            localStorage.setItem('hasVisitedJobxBharat', 'true');
            navigate('/search');
          }}
        />
      );
    }
  } catch (e) {
    // If anything goes wrong parsing URL, fall back to showing landing
    return (
      <LandingPage
        isDarkMode={isDarkMode}
        onSignIn={() => {
          setShowLanding(false);
          localStorage.setItem('hasVisitedJobxBharat', 'true');
          navigate('/login');
        }}
        onStartSearching={() => {
          setShowLanding(false);
          localStorage.setItem('hasVisitedJobxBharat', 'true');
          navigate('/search');
        }}
      />
    );
  }
}

  // Show auth component ONLY for password reset flows OR if user is on /login or /signup routes
  const currentPath = locationHook.pathname;
  const isOnAuthRoute = currentPath === '/login' || currentPath === '/signup';

  if (!isAuthenticated && (showResetPasswordConfirm || showForgotPassword || isOnAuthRoute)) {
      return (
    <div className={`min-h-screen transition-colors duration-300 ${c.pageBg} ${c.text}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="fixed top-6 right-6 z-50">
          <motion.button
            onClick={toggleDarkMode}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className={`p-3 rounded-full shadow-lg ${isDarkMode
              ? 'bg-[#23232A] text-[#06B6D4] hover:bg-[#252535]'
              : 'bg-white text-[#21808D] hover:bg-gray-100'
            }`}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </motion.button>
        </div>

        {renderAuthComponent()}
      </div>
    </div>
  );

  }

  return (
    <div className={`min-h-screen transition-colors duration-300`}>
      <Routes>
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/walkin-drives" element={<WalkinDrives user={user} isAuthenticated={isAuthenticated} onLogout={handleLogout} />} />
        <Route path="/walkin-drives/admin" element={<WalkinDriveAdmin />} />
        <Route path="/walkin-drives/:slug" element={<WalkinDriveDetail user={user} isAuthenticated={isAuthenticated} onLogout={handleLogout} />} />
        <Route path="/about" element={<About onBack={handleBackToHome} isDarkMode={isDarkMode} />} />
        <Route path="/gov-jobs" element={<GovernmentJobs onBack={handleBackToHome} isDarkMode={isDarkMode} />} />
        <Route path="/social-job" element={
          isAuthenticated
            ? <SocialJobView onBack={handleBackToHome} isDarkMode={isDarkMode} />
            : <SocialJobLoginPrompt isDarkMode={isDarkMode} c={c} toggleDarkMode={toggleDarkMode} onLoginClick={() => navigate('/login')} />
        } />
        <Route path="/resume-builder" element={<ResumeBuilderPage isDarkMode={isDarkMode} isAuthenticated={isAuthenticated} user={user} />} />
        <Route path="/verify-email/:token" element={<EmailVerification isDarkMode={isDarkMode} />} />
        <Route path="/feedback" element={renderContent()} />
        <Route path="/contact" element={renderContent()} />
        <Route path="/job-alert" element={renderContent()} />
        <Route path="/profile" element={isAuthenticated ? <ProfilePage isDarkMode={isDarkMode} user={user} /> : <Navigate to="/login" replace />} />
        <Route path="/search" element={
          <SearchPage
            user={user}
            isAuthenticated={isAuthenticated}
            onLogout={handleLogout}
          />
        } />
        {/* SEO landing pages for aggregated jobs (Phase 5b) */}
        <Route path="/jobs-in-:city" element={
          <SearchPage
            user={user}
            isAuthenticated={isAuthenticated}
            onLogout={handleLogout}
            landing="location"
          />
        } />
        <Route path="/jobs/:category" element={
          <SearchPage
            user={user}
            isAuthenticated={isAuthenticated}
            onLogout={handleLogout}
            landing="category"
          />
        } />
        <Route path="/login" element={
          <div className={`min-h-screen transition-colors duration-300 ${c.pageBg} ${c.text}`}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="fixed top-6 right-6 z-50">
                <motion.button
                  onClick={toggleDarkMode}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className={`p-3 rounded-full shadow-lg ${isDarkMode
                    ? 'bg-[#23232A] text-[#06B6D4] hover:bg-[#252535]'
                    : 'bg-white text-[#21808D] hover:bg-gray-100'
                  }`}
                >
                  {isDarkMode ? '☀️' : '🌙'}
                </motion.button>
              </div>
              <Login
                onLoginSuccess={handleLoginSuccess}
                onRegisterClick={() => navigate('/signup')}
                onForgotPasswordClick={() => setShowForgotPassword(true)}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        } />
        <Route path="/signup" element={
          <div className={`min-h-screen transition-colors duration-300 ${c.pageBg} ${c.text}`}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="fixed top-6 right-6 z-50">
                <motion.button
                  onClick={toggleDarkMode}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className={`p-3 rounded-full shadow-lg ${isDarkMode
                    ? 'bg-[#23232A] text-[#06B6D4] hover:bg-[#252535]'
                    : 'bg-white text-[#21808D] hover:bg-gray-100'
                  }`}
                >
                  {isDarkMode ? '☀️' : '🌙'}
                </motion.button>
              </div>
              <Signup
                onBackToLogin={() => navigate('/login')}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        } />
        <Route path="/" element={isAuthenticated ? <Navigate to="/search" replace /> : renderContent()} />
      </Routes>
    </div>
  );
}

export default App;
